import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { DuoClairMark } from "@/components/DuoClairLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err, "Accesso non riuscito."));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-5 relative" data-testid="admin-login-page">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[50vw] h-[30vh] rounded-full bg-gold/10 blur-[120px] pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-sm luxury-card p-8 sm:p-10"
      >
        <div className="absolute inset-x-8 top-0 gold-hairline" />
        <div className="flex flex-col items-center mb-8">
          <DuoClairMark className="w-12 h-12 mb-4" />
          <h1 className="font-serif text-2xl text-slate-100">Area Riservata</h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-2">DuoClair · Solo Partner</p>
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-zinc-300 text-sm">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              data-testid="admin-login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@duoclair.it"
              className="input-lux"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-zinc-300 text-sm">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              data-testid="admin-login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              className="input-lux"
              required
            />
          </div>
          {error && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full h-12 rounded-xl bg-gradient-to-r from-gold-dark via-gold to-gold-light text-black font-semibold hover:opacity-95 transition-all duration-300 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="flex items-center gap-2"><Lock className="w-4 h-4" /> Accedi</span>}
          </Button>
        </form>
        <div className="mt-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-zinc-600 hover:text-gold transition-colors" data-testid="admin-login-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Torna al sito
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
